import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  BulkAssignCaseManagerResult,
  BulkRemoveCaseManagerResult,
} from '@/services/api/caseManagerService';
import {
  useBulkAssignCaseManagerMutation,
  useBulkRemoveCaseManagerMutation,
  useGetFirmCaseManagersQuery,
} from '@/services/api/caseManagerService';
import { MatterListItem } from '@/services/types/matter-types';
import { getApiErrorMessage } from '@/utils/api-error';

interface BulkAssignCaseManagerModalProps {
  open: boolean;
  onClose: () => void;
  selectedMatters: MatterListItem[];
  onSuccess?: () => void;
}

type Mode = 'assign' | 'remove';
type Step = 'select' | 'result';

export const BulkAssignCaseManagerModal = ({
  open,
  onClose,
  selectedMatters,
  onSuccess,
}: BulkAssignCaseManagerModalProps) => {
  const [step, setStep] = useState<Step>('select');
  const [mode, setMode] = useState<Mode>('assign');
  const [selectedCaseManagerId, setSelectedCaseManagerId] =
    useState<string>('');
  const [autoTaskAssignment, setAutoTaskAssignment] = useState(true);
  const [revertTasksToMp, setRevertTasksToMp] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignResultData, setAssignResultData] =
    useState<BulkAssignCaseManagerResult | null>(null);
  const [removeResultData, setRemoveResultData] =
    useState<BulkRemoveCaseManagerResult | null>(null);

  // Group matters by firm
  const mattersByFirm = useMemo(() => {
    return selectedMatters.reduce((grouped, matter) => {
      const { id } = matter.firm;
      const existing = grouped.get(id);
      if (existing) {
        existing.matters.push(matter);
      } else {
        grouped.set(id, { firmName: matter.firm.name, matters: [matter] });
      }
      return grouped;
    }, new Map<string, { firmName: string; matters: MatterListItem[] }>());
  }, [selectedMatters]);

  // Check if all matters are from the same firm
  const singleFirm = mattersByFirm.size === 1;
  const firmId = singleFirm ? Array.from(mattersByFirm.keys())[0] : null;
  const firmName = singleFirm
    ? Array.from(mattersByFirm.values())[0].firmName
    : null;

  // Count matters that have a case manager assigned (for remove mode info)
  const mattersWithCaseManager = useMemo(
    () => selectedMatters.filter((m) => m.case_manager !== null).length,
    [selectedMatters],
  );

  // Fetch case managers for the firm (only if single firm and assign mode)
  const { data: caseManagers = [], isLoading: isLoadingCaseManagers } =
    useGetFirmCaseManagersQuery(
      { firmId: firmId ?? '' },
      {
        skip: !firmId || !open || mode === 'remove',
      },
    );

  const [bulkAssign, { isLoading: isAssigning }] =
    useBulkAssignCaseManagerMutation();
  const [bulkRemove, { isLoading: isRemoving }] =
    useBulkRemoveCaseManagerMutation();

  const isSubmitting = isAssigning || isRemoving;

  // When CM selection changes, default auto_task_assignment from the CM's config
  const selectedCaseManager = useMemo(
    () => caseManagers.find((cm) => cm.id === selectedCaseManagerId),
    [caseManagers, selectedCaseManagerId],
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep('select');
      setMode('assign');
      setSelectedCaseManagerId('');
      setAutoTaskAssignment(true);
      setRevertTasksToMp(true);
      setError(null);
      setAssignResultData(null);
      setRemoveResultData(null);
    }
  }, [open]);

  // Update auto_task_assignment when CM changes
  useEffect(() => {
    if (selectedCaseManager) {
      setAutoTaskAssignment(selectedCaseManager.case_auto_assign ?? true);
    }
  }, [selectedCaseManager]);

  // Reset state when switching modes
  useEffect(() => {
    if (mode === 'remove') {
      setSelectedCaseManagerId('');
    }
    setError(null);
  }, [mode]);

  const handleAssign = async () => {
    if (!selectedCaseManagerId) return;

    try {
      const result = await bulkAssign({
        data: {
          case_manager_id: selectedCaseManagerId,
          matter_ids: selectedMatters.map((m) => m.id),
          auto_task_assignment: autoTaskAssignment,
        },
      }).unwrap();

      setAssignResultData(result);
      setStep('result');
      setError(null);

      if (result.failed === 0) {
        toast.success(
          `Case manager assigned to ${result.successful} matter${result.successful !== 1 ? 's' : ''}`,
        );
      } else {
        toast.warning(
          `Assigned ${result.successful} matter${result.successful !== 1 ? 's' : ''}, ${result.failed} failed`,
        );
      }
      onSuccess?.();
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          'Failed to assign case manager. Please try again.',
        ),
      );
    }
  };

  const handleRemove = async () => {
    try {
      const result = await bulkRemove({
        data: {
          matter_ids: selectedMatters.map((m) => m.id),
          revert_tasks_to_mp: revertTasksToMp,
        },
      }).unwrap();

      setRemoveResultData(result);
      setStep('result');
      setError(null);

      if (result.failed === 0) {
        if (result.successful === 0) {
          toast.info('No matters had a case manager to remove.');
        } else {
          toast.success(
            `Case manager removed from ${result.successful} matter${result.successful !== 1 ? 's' : ''}`,
          );
        }
      } else {
        toast.warning(
          `Removed ${result.successful} matter${result.successful !== 1 ? 's' : ''}, ${result.failed} failed`,
        );
      }
      onSuccess?.();
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          'Failed to remove case manager. Please try again.',
        ),
      );
    }
  };

  const handleSubmit = () => {
    if (mode === 'assign') {
      handleAssign();
    } else {
      handleRemove();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const noCaseManagers = !isLoadingCaseManagers && caseManagers.length === 0;

  const isAssignDisabled =
    !singleFirm ||
    !selectedCaseManagerId ||
    isSubmitting ||
    isLoadingCaseManagers;

  const isRemoveDisabled = !singleFirm || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Case Manager</DialogTitle>
          <DialogDescription>
            Assign or remove a case manager for the selected matters.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Multi-firm warning - only show on select step */}
        {!singleFirm && step === 'select' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Selected matters belong to {mattersByFirm.size} different firms.
              Case manager operations can only be done for matters from a single
              firm. Please filter your selection to one firm.
            </AlertDescription>
          </Alert>
        )}

        {/* Step: Select */}
        {step === 'select' && singleFirm && (
          <div className="space-y-3 py-3">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-600">
                <span className="text-gray-900">{selectedMatters.length}</span>{' '}
                matters selected from{' '}
                <span className="text-gray-900">{firmName}</span>
              </p>
            </div>

            {/* Mode selector */}
            <RadioGroup
              value={mode}
              onValueChange={(value) => setMode(value as Mode)}
              className="flex gap-4"
              disabled={isSubmitting}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="assign" id="mode-assign" />
                <Label htmlFor="mode-assign" className="cursor-pointer font-medium">
                  Assign
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="remove" id="mode-remove" />
                <Label htmlFor="mode-remove" className="cursor-pointer font-medium">
                  Remove
                </Label>
              </div>
            </RadioGroup>

            {/* Assign mode content */}
            {mode === 'assign' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="case-manager" className="font-medium">
                    Case Manager
                  </Label>
                  {isLoadingCaseManagers && (
                    <p className="text-sm font-medium text-gray-600">
                      Loading case managers...
                    </p>
                  )}
                  {noCaseManagers && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No case managers found for this firm. Configure case
                        manager roles in the firm settings.
                      </AlertDescription>
                    </Alert>
                  )}
                  {!isLoadingCaseManagers && caseManagers.length > 0 && (
                    <Select
                      value={selectedCaseManagerId}
                      onValueChange={setSelectedCaseManagerId}
                    >
                      <SelectTrigger id="case-manager">
                        <SelectValue placeholder="Select a case manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {caseManagers.map((cm) => (
                          <SelectItem key={cm.id} value={cm.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {cm.display_name}
                              </span>
                              {cm.display_name !== cm.email && (
                                <span className="text-xs font-medium text-gray-600">
                                  {cm.email}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

            {/* Auto-assign tasks toggle */}
            {selectedCaseManagerId && (
              <div className="space-y-1">
                <CheckboxField>
                  <Checkbox
                    checked={autoTaskAssignment}
                    onChange={(checked) =>
                      setAutoTaskAssignment(Boolean(checked))
                    }
                    color="indigo"
                  />
                  <Label className="cursor-pointer text-sm font-medium">
                    Auto-assign tasks
                  </Label>
                </CheckboxField>
                <p className="ml-7 text-xs font-medium text-gray-600">
                  Reassigns existing incomplete tasks and auto-assigns new
                  tasks to the case manager.
                </p>
              </div>
            )}
              </>
            )}

            {/* Remove mode content */}
            {mode === 'remove' && (
              <>
                {mattersWithCaseManager === 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      None of the selected matters have a case manager assigned.
                    </AlertDescription>
                  </Alert>
                )}

                {mattersWithCaseManager > 0 && (
                  <p className="text-sm font-medium text-gray-600">
                    {mattersWithCaseManager} of {selectedMatters.length} selected
                    matter{selectedMatters.length !== 1 ? 's' : ''} ha
                    {mattersWithCaseManager === 1 ? 's' : 've'} a case manager
                    assigned.
                  </p>
                )}

                <div className="space-y-1">
                  <CheckboxField>
                    <Checkbox
                      checked={revertTasksToMp}
                      onChange={(checked) =>
                        setRevertTasksToMp(Boolean(checked))
                      }
                      color="indigo"
                    />
                    <Label className="cursor-pointer text-sm font-medium">
                      Revert tasks to managing paralegal
                    </Label>
                  </CheckboxField>
                  <p className="ml-7 text-xs font-medium text-gray-600">
                    Reassigns the case manager&apos;s incomplete tasks back to
                    the managing paralegal.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step: Assign Result */}
        {step === 'result' && mode === 'assign' && assignResultData && (
          <div className="space-y-3 py-3">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Case manager assignment complete.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {assignResultData.successful}
                </p>
                <p className="text-xs font-medium text-gray-600">
                  Matters updated
                </p>
              </div>
              <div className="rounded-md border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {assignResultData.total_tasks_reassigned}
                </p>
                <p className="text-xs font-medium text-gray-600">
                  Tasks reassigned
                </p>
              </div>
            </div>

            {assignResultData.skipped > 0 && (
              <p className="text-xs font-medium text-gray-600">
                {assignResultData.skipped} matter
                {assignResultData.skipped !== 1 ? 's' : ''} skipped (already
                assigned).
              </p>
            )}

            {assignResultData.failed > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {assignResultData.failed} matter(s) failed. Please try again
                  or assign individually.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Step: Remove Result */}
        {step === 'result' && mode === 'remove' && removeResultData && (
          <div className="space-y-3 py-3">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Case manager removal complete.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {removeResultData.successful}
                </p>
                <p className="text-xs font-medium text-gray-600">
                  Matters updated
                </p>
              </div>
              <div className="rounded-md border border-gray-200 p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {removeResultData.total_tasks_reverted}
                </p>
                <p className="text-xs font-medium text-gray-600">
                  Tasks reverted
                </p>
              </div>
            </div>

            {removeResultData.skipped > 0 && (
              <p className="text-xs font-medium text-gray-600">
                {removeResultData.skipped} matter
                {removeResultData.skipped !== 1 ? 's' : ''} skipped (no case
                manager assigned).
              </p>
            )}

            {removeResultData.failed > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {removeResultData.failed} matter(s) failed. Please try again
                  or remove individually.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <>
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  mode === 'assign' ? isAssignDisabled : isRemoveDisabled
                }
                variant={mode === 'remove' ? 'destructive' : 'default'}
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isSubmitting
                  ? mode === 'assign'
                    ? 'Assigning...'
                    : 'Removing...'
                  : mode === 'assign'
                    ? 'Assign'
                    : 'Remove'}
              </Button>
            </>
          )}

          {step === 'result' && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
